import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _get from "lodash/get";
import _uniq from "lodash/uniq";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es6.array.filter.js";
import { combineReducers } from 'redux';
import * as Actions from './actions';
/**
 * @typedef {Object} CompareCardsState
 * @property {number[]} cardIDsToCompare - A list of card-ids.
 * @property {Object.<number, Object>} cardsToCompare - A dict mapping card-ids to card objects.
 * @property {*[]} favoriteCombos - Mystery list. Never seen anything in it.
 */

/**
 * @constant
 * @type {CompareCardsState}
 */

export var defaultState = {
  cardIDsToCompare: [],
  cardsToCompare: {},
  favoriteCombos: []
};
/**
 * Returns a list of card-ids. Depending on the action type, may expect action to have either a
 * list of card-objects, a single card object, a single card-id, a list of card-ids, or nothing
 * at all (when clearing the list).
 *
 * @param {number[]} state - current list of card-ids.
 * @param {Object} action - See description or source to understand expected shape.
 * @returns {number[]} - new list of card-ids.
 */

export var cardIDsToCompare = function cardIDsToCompare() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState.cardIDsToCompare;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  switch (action.type) {
    // Expects a list of card objects in the action.
    case Actions.ADD_CARDS_TO_COMPARE:
      return _uniq(state.concat((action.cards || []).map(function (card) {
        return card.id;
      })));
    // Expects a single card-id

    case Actions.REMOVE_CARD_FROM_COMPARE:
      {
        var idToExclude = action.id;
        return state.filter(function (id) {
          return id !== idToExclude;
        });
      }
    // Expects a list of card-ids.

    case Actions.RECEIVE_COMPARE_CARDS:
      return _get(action, 'compareCards.cardIDsToCompare', []);

    case Actions.CLEAR_COMPARED_CARDS:
      return [];
    // Expects a list of card-ids.

    case Actions.SET_COMPARED_CARD_IDS:
      return action.cardIds;
  }

  return state;
};
/**
 * Returns an object mapping card-ids to card objects. Depending on the action type, may expect
 * action to have either a list of card-objects, a single card object, or nothing at all (when
 * clearing the list).
 *
 * @param {Object.<number, Object>} state - current map of card objects.
 * @param {Object} action - See description or source to understand expected shape.
 * @returns {Object.<number, Object>} - new map of card objects.
 */

export var cardsToCompare = function cardsToCompare() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState.cardsToCompare;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  switch (action.type) {
    case Actions.ADD_CARDS_TO_COMPARE:
      {
        // - Expects a list of card objects in the action.
        // - The following can be done more succintly using reduce but I feel that needlessly
        // involves repeatedly cloning objects (which is way more expensive than just filling slots
        // in a single object).
        var idToCardMap = {};
        (action.cards || []).forEach(function (card) {
          idToCardMap[card.id] = card;
        }); // eslint-disable-line

        return _objectSpread(_objectSpread({}, state), idToCardMap);
      }

    case Actions.REMOVE_CARD_FROM_COMPARE:
      {
        // Expects a single card object.
        var _idToCardMap = _objectSpread({}, state);

        delete _idToCardMap[action.id];
        return _idToCardMap;
      }
    // Expects a map of card ids to card objects.

    case Actions.RECEIVE_COMPARE_CARDS:
      return _get(action, 'compareCards.cardsToCompare', {});

    case Actions.CLEAR_COMPARED_CARDS:
      return {};
    // Expects a map of card ids to card objects.

    case Actions.SET_COMPARED_CARD_DATA:
      return action.cards;
  }

  return state;
};
/**
 * @param {*[]} state - an array of indeterminate value (never seen it containing anything).
 * @param {Object} action - Where type is `RECEIVE_COMPARE_CARDS` and contains a property `favoriteCombos[]`.
 * @return {*[]} - return `action.favoriteCombos`.
 */

export var favoriteCombos = function favoriteCombos() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState.favoriteCombos;
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return action.type === Actions.RECEIVE_COMPARE_CARDS ? _get(action, 'compareCards.favoriteCombos', []) : state;
};
/**
 * We may add more actions here in the future, which could be ORed to get a single busy status.
 * @todo verify if ANYONE uses this. Otherwise nix it.
 * @param {boolean} state=false -
 * @param {Action} action - An action containing a boolean `isFetching` property.
 * @returns {boolean} - true if we are fetching data for compare cards.
 */

export var isCompareCardsBusy = function isCompareCardsBusy() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return action.type === Actions.SET_FETCHING_CARD_DATA ? action.isFetching : state;
};
/**
 * This is the default reducer. It becomes store.compareCards in cc-client.
 * @function
 * @param {CompareCardsState} -
 * @param {Action} -
 * @returns {CompareCardsState} -
 */

export default combineReducers({
  cardIDsToCompare: cardIDsToCompare,
  cardsToCompare: cardsToCompare,
  favoriteCombos: favoriteCombos,
  isCompareCardsBusy: isCompareCardsBusy
});